import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, RefreshCw, Download, Trash2, Copy, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["draft", "confirmed", "paid", "cancelled"];
const PAY_METHODS = ["cash", "bank_transfer", "upi", "card", "cheque", "other"];

const STATUS_STYLES = {
  draft:     "bg-gray-500/15 text-gray-300 border-gray-500/40",
  confirmed: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  paid:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

const emptyForm = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  destination_name: "",
  start_date: "",
  end_date: "",
  adults: 2,
  children: 0,
  infants: 0,
  package_type: "premium",
  flight_costs: 0,
  hotel_costs: 0,
  transfer_costs: 0,
  itinerary_cost: 0,
  misc_cost: 0,
  misc_label: "",
  tax_rate: 0.18,
  payment_method: "cash",
  transaction_ref: "",
  amount_paid: 0,
  status: "confirmed",
  notes: "",
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// Admin-only tab for capturing offline bookings (walk-ins, phone leads,
// corporate accounts) and issuing a downloadable tax invoice. Purely a
// documentation surface — separate from the online booking flow so it
// doesn't leak into EMI reconciliation or cancellation windows.
export default function InvoiceGeneratorTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get(`${API}/admin/offline-bookings`, { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const asNumber = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

  // Live tally so the admin sees the running total as they enter costs.
  const subtotal = asNumber(form.flight_costs) + asNumber(form.hotel_costs)
    + asNumber(form.transfer_costs) + asNumber(form.itinerary_cost)
    + asNumber(form.misc_cost);
  const tax = Math.round(subtotal * asNumber(form.tax_rate));
  const total = subtotal + tax;
  const balanceDue = Math.max(total - asNumber(form.amount_paid), 0);

  const create = async () => {
    if (!form.customer_name.trim() || !form.customer_phone.trim()
      || !form.destination_name.trim() || !form.start_date || !form.end_date) {
      toast.error("Please fill customer name, phone, destination and dates");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        adults: asNumber(form.adults) || 1,
        children: asNumber(form.children),
        infants: asNumber(form.infants),
        flight_costs: asNumber(form.flight_costs),
        hotel_costs: asNumber(form.hotel_costs),
        transfer_costs: asNumber(form.transfer_costs),
        itinerary_cost: asNumber(form.itinerary_cost),
        misc_cost: asNumber(form.misc_cost),
        tax_rate: asNumber(form.tax_rate),
        amount_paid: asNumber(form.amount_paid),
        customer_email: form.customer_email || undefined,
        misc_label: form.misc_label || undefined,
        transaction_ref: form.transaction_ref || undefined,
        notes: form.notes || undefined,
      };
      const { data } = await axios.post(`${API}/admin/offline-bookings`, payload);
      toast.success(`Booking ${data.id} created`);
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete offline booking ${id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/offline-bookings/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/offline-bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const downloadInvoice = async (id) => {
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${API}/admin/offline-bookings/${id}/invoice.pdf?token=${encodeURIComponent(token)}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const copyId = async (id) => {
    try { await navigator.clipboard.writeText(id); toast.success(`Copied ${id}`); }
    catch { toast.error("Couldn't copy"); }
  };

  return (
    <div data-testid="invoice-generator-tab" className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="offline-filter-all"
          className={`px-3 py-1.5 text-xs rounded-full border transition-all ${filter === "" ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/40"}`}
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`offline-filter-${s}`}
            className={`px-3 py-1.5 text-xs rounded-full border capitalize transition-all ${filter === s ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/40"}`}
          >
            {s}
          </button>
        ))}
        <div className="flex-1" />
        <Button variant="ghost" onClick={load} disabled={loading} className="text-white/70 hover:text-white" data-testid="offline-refresh-button">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button
          onClick={() => setShowForm((s) => !s)}
          className="bg-[#D4AF37] text-black hover:bg-[#E5C468]"
          data-testid="offline-new-booking-button"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {showForm ? "Close form" : "New booking"}
        </Button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-white/[0.03] p-5 space-y-5" data-testid="offline-booking-form">
          {/* Customer */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Customer</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-white/70 text-xs">Name*</Label>
                <Input data-testid="offline-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Email</Label>
                <Input data-testid="offline-email" type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Phone*</Label>
                <Input data-testid="offline-phone" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+91 98765 43210" className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
            </div>
          </div>

          {/* Trip */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Trip</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-white/70 text-xs">Destination*</Label>
                <Input data-testid="offline-destination" value={form.destination_name} onChange={(e) => set("destination_name", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Start date*</Label>
                <Input data-testid="offline-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">End date*</Label>
                <Input data-testid="offline-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Package</Label>
                <select data-testid="offline-package" value={form.package_type} onChange={(e) => set("package_type", e.target.value)} className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2 h-10">
                  {["luxury", "premium", "basic", "custom"].map((p) => <option key={p} value={p} className="bg-black">{p}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white/70 text-xs">Adults</Label>
                <Input data-testid="offline-adults" type="number" min={1} max={30} value={form.adults} onChange={(e) => set("adults", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Children</Label>
                <Input data-testid="offline-children" type="number" min={0} max={30} value={form.children} onChange={(e) => set("children", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Infants</Label>
                <Input data-testid="offline-infants" type="number" min={0} max={10} value={form.infants} onChange={(e) => set("infants", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
            </div>
          </div>

          {/* Cost breakdown */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Cost breakdown (INR, pre-tax)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-white/70 text-xs">Flights</Label>
                <Input data-testid="offline-flight-costs" type="number" min={0} value={form.flight_costs} onChange={(e) => set("flight_costs", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Hotels</Label>
                <Input data-testid="offline-hotel-costs" type="number" min={0} value={form.hotel_costs} onChange={(e) => set("hotel_costs", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Transfers</Label>
                <Input data-testid="offline-transfer-costs" type="number" min={0} value={form.transfer_costs} onChange={(e) => set("transfer_costs", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Itinerary</Label>
                <Input data-testid="offline-itinerary-cost" type="number" min={0} value={form.itinerary_cost} onChange={(e) => set("itinerary_cost", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Misc</Label>
                <Input data-testid="offline-misc-cost" type="number" min={0} value={form.misc_cost} onChange={(e) => set("misc_cost", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Misc label</Label>
                <Input data-testid="offline-misc-label" value={form.misc_label} onChange={(e) => set("misc_label", e.target.value)} placeholder="e.g. Visa fee" className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Tax rate</Label>
                <Input data-testid="offline-tax-rate" type="number" step="0.01" min={0} max={1} value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
                <p className="text-[10px] text-white/40 mt-1">Default 0.18 (18% IGST)</p>
              </div>
            </div>
            <div className="mt-3 rounded-md border border-white/10 bg-white/[0.03] p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
              <span className="text-white/60">Subtotal: <b className="text-white">{fmtINR(subtotal)}</b></span>
              <span className="text-white/60">Tax: <b className="text-white">{fmtINR(tax)}</b></span>
              <span className="text-white/60">Total: <b className="text-[#D4AF37]" data-testid="offline-total-preview">{fmtINR(total)}</b></span>
              <span className="text-white/60">Balance due: <b className="text-white">{fmtINR(balanceDue)}</b></span>
            </div>
          </div>

          {/* Payment */}
          <div>
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Payment</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-white/70 text-xs">Method</Label>
                <select data-testid="offline-payment-method" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2 h-10">
                  {PAY_METHODS.map((p) => <option key={p} value={p} className="bg-black">{p.replace("_", " ")}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/70 text-xs">Transaction ref</Label>
                <Input data-testid="offline-txn-ref" value={form.transaction_ref} onChange={(e) => set("transaction_ref", e.target.value)} placeholder="UTR / cheque no / receipt" className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Amount received</Label>
                <Input data-testid="offline-amount-paid" type="number" min={0} value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white" />
              </div>
              <div>
                <Label className="text-white/70 text-xs">Status</Label>
                <select data-testid="offline-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2 h-10">
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-4">
                <Label className="text-white/70 text-xs">Notes (internal)</Label>
                <textarea data-testid="offline-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="text-white/70 hover:text-white" data-testid="offline-cancel-button">Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#E5C468]" data-testid="offline-save-button">
              {saving ? "Saving…" : "Create booking"}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {items.length === 0 && !loading && (
          <p className="p-6 text-center text-sm text-white/50">No offline bookings yet.</p>
        )}
        {items.map((b) => (
          <div key={b.id} className="border-b border-white/10 last:border-0 px-4 py-3" data-testid={`offline-row-${b.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => copyId(b.id)} title="Copy booking ID" className="font-mono text-[#D4AF37] hover:text-white text-sm flex items-center gap-1" data-testid={`offline-copy-${b.id}`}>
                {b.id} <Copy className="w-3 h-3 opacity-60" />
              </button>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[b.status] || STATUS_STYLES.confirmed}`}>{b.status}</span>
              <span className="text-white font-medium">{b.customer_name}</span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-white/70 text-xs">{b.destination_name}</span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-white/70 text-xs">{(b.start_date || "").slice(0, 10)} → {(b.end_date || "").slice(0, 10)}</span>
              <div className="flex-1" />
              <span className="text-white font-medium">{fmtINR(b.total_cost)}</span>
              {b.balance_due > 0 && (
                <span className="text-yellow-300 text-xs">Balance {fmtINR(b.balance_due)}</span>
              )}
              <Button size="sm" variant="ghost" onClick={() => downloadInvoice(b.id)} className="text-[#D4AF37] hover:text-white" data-testid={`offline-invoice-${b.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> Invoice
              </Button>
              {b.status !== "paid" && (
                <Button size="sm" variant="ghost" onClick={() => updateStatus(b.id, "paid")} className="text-emerald-300 hover:text-white" data-testid={`offline-mark-paid-${b.id}`}>
                  Mark paid
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(b.id)} className="text-red-300 hover:text-red-200" data-testid={`offline-delete-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="mt-1 text-[11px] text-white/40 flex flex-wrap gap-x-4">
              <span>{b.adults}A + {b.children}C + {b.infants}I · {b.package_type}</span>
              <span>{(b.payment_method || "").replace("_", " ")}{b.transaction_ref ? ` · ${b.transaction_ref}` : ""}</span>
              <span>Created {fmtDate(b.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
